import type { LaunchListItem, LaunchStatus, NextAction } from './types';
import { launchStatusLabel, nextActionLabel } from './status';

interface LaunchOverviewProps {
  items: LaunchListItem[];
  total: number;
  limit: number;
  offset: number;
  loading: boolean;
}

const STATUS_BUCKETS: { label: string; statuses: LaunchStatus[] }[] = [
  { label: 'Active', statuses: ['paid_active'] },
  { label: 'In progress', statuses: ['checkout_pending', 'payment_recorded', 'draft'] },
  { label: 'Paused', statuses: ['paid_paused'] },
  { label: 'Ended', statuses: ['failed', 'cancelled'] },
];

const NEXT_ACTION_BUCKETS: { label: string; actions: NextAction[] }[] = [
  { label: 'Legacy non-demo approval', actions: ['approve_launch'] },
  { label: 'Needs attention', actions: ['operator_recovery'] },
  { label: 'Waiting on clinic', actions: ['wait_for_checkout'] },
  { label: 'Waiting on system', actions: ['wait_for_subscription_webhook'] },
  { label: 'Active', actions: ['paid_active'] },
];

export function LaunchOverview({ items, total, loading }: LaunchOverviewProps) {
  if (loading && items.length === 0) {
    return (
      <div className="overview">
        <div className="loading" data-testid="overview-loading">Loading overview…</div>
      </div>
    );
  }

  if (total === 0) {
    return (
      <div className="panel overview">
        <div className="empty-state" data-testid="overview-empty">
          <h3>No demo clinics yet</h3>
          <p>Use Clinic Demo Creation to create the first demo clinic.</p>
        </div>
      </div>
    );
  }

  const isPageScoped = items.length < total;
  const pageLabel = isPageScoped ? `showing first ${items.length} of ${total}` : null;

  const statusCounts = Object.fromEntries(
    STATUS_BUCKETS.map((bucket) => [
      bucket.label,
      items.filter((i) => bucket.statuses.includes(i.status)).length,
    ]),
  );

  const actionCounts = Object.fromEntries(
    NEXT_ACTION_BUCKETS.map((bucket) => [
      bucket.label,
      items.filter((i) => bucket.actions.includes(i.next_action)).length,
    ]),
  );

  return (
    <div className="panel overview" data-testid="launch-overview">
      <div className="overview-headline">
        <span className="overview-headline-count" data-testid="overview-total">{total}</span>
        <span className="overview-headline-label">total demo clinics</span>
      </div>

      {pageLabel && (
        <div className="overview-scope-note" data-testid="overview-scope-note">
          Status breakdown reflects {pageLabel}, totals may differ across all demo clinics.
        </div>
      )}

      <div className="copy-fallback">Select a demo clinic to view payment-link state, safe history, and access actions.</div>

      <div className="overview-section-title">By status</div>
      <div className="status-grid" data-testid="overview-status-grid">
        {STATUS_BUCKETS.map((bucket) => (
          <div className="status-card" key={bucket.label} data-testid={`overview-status-${bucket.label.toLowerCase().replace(' ', '-')}`}>
            <h4>{bucket.label}</h4>
            <p>{statusCounts[bucket.label]}</p>
          </div>
        ))}
      </div>

      <div className="overview-section-title">By next action</div>
      <div className="status-grid" data-testid="overview-action-grid">
        {NEXT_ACTION_BUCKETS.map((bucket) => (
          <div className="status-card" key={bucket.label} data-testid={`overview-action-${bucket.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <h4>{bucket.label}</h4>
            <p>{actionCounts[bucket.label]}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// Re-export helpers for use in tests
export { launchStatusLabel, nextActionLabel };
